import React from 'react';
import { Task } from '../../../types';
import { GuidedProgressBar } from './GuidedProgressBar';

export interface GuidedTaskBriefProps {
  task: Task;
  done: number;
  total: number;
  /** Tapping "Start task" → opens the capture screen. */
  onStart: () => void;
  /** Tapping the back-arrow at the top — usually returns to welcome
      (mid-flow) or to rest (between tasks). The shell decides. */
  onBack: () => void;
}

/** Human-friendly label per task media-type. The ember icon already
    conveys the media; the label is just the verbal echo. */
function mediaLabel(mediaType?: string): string {
  switch (mediaType) {
    case 'audio': return 'Voice note';
    case 'photo': return 'Photo';
    case 'video': return 'Video';
    case 'text': return 'Text';
    case 'screen': return 'Screen';
    case 'guided': return 'Guided capture';
    case 'signification': return 'Reflection';
    default: return 'Task';
  }
}

/** Per-media-type icon, mirrors the welcome chip set so the language
    stays consistent across screens. */
function MediaTypeIcon({ mediaType }: { mediaType?: string }) {
  const common = {
    fill: 'none', stroke: 'currentColor',
    strokeWidth: 2, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const,
  };
  switch (mediaType) {
    case 'audio':
      return (
        <svg viewBox="0 0 24 24" {...common}>
          <rect x="9" y="2" width="6" height="12" rx="3" />
          <path d="M5 11a7 7 0 0 0 14 0M12 18v3" />
        </svg>
      );
    case 'photo':
      return (
        <svg viewBox="0 0 24 24" {...common}>
          <path d="M5 7h4l1-2h4l1 2h4v12H5z" />
          <circle cx="12" cy="13" r="3.5" />
        </svg>
      );
    case 'video':
      return (
        <svg viewBox="0 0 24 24" {...common}>
          <rect x="3" y="6" width="13" height="12" rx="2" />
          <path d="M16 10l5-3v10l-5-3z" />
        </svg>
      );
    case 'text':
      return (
        <svg viewBox="0 0 24 24" {...common}>
          <path d="M5 5h14M5 10h14M5 15h9" />
        </svg>
      );
    case 'signification':
      return (
        <svg viewBox="0 0 24 24" {...common}>
          <circle cx="12" cy="12" r="8" />
          <circle cx="12" cy="12" r="2.5" fill="currentColor" stroke="none" />
        </svg>
      );
    default:
      return (
        <svg viewBox="0 0 24 24" {...common}>
          <circle cx="12" cy="12" r="8" />
        </svg>
      );
  }
}

function formatTarget(estSec?: number): string | null {
  if (!estSec || estSec <= 0) return null;
  const min = Math.round(estSec / 60);
  if (min < 1) return `${estSec} sec target`;
  return `${min} min target`;
}

/**
 * Strip the prompt into a "headline" + "helper" pair. The designer's
 * mock used `<em>` tags inside the headline to ember-accent a key
 * word (e.g. "The song that <em>anchors</em> it.") — researcher
 * prompts are plain strings today, so we render the whole prompt as
 * headline. If the task has a `helperText` field we'll show that;
 * otherwise hide the helper section.
 */
function renderPromptParts(task: Task): { headline: string; helper: string | null } {
  const headline = task.title?.trim() || task.prompt?.trim() || 'Task';
  // If both title and prompt exist, treat prompt as helper text.
  const helper = task.title && task.prompt && task.title.trim() !== task.prompt.trim()
    ? task.prompt.trim()
    : null;
  return { headline, helper };
}

export const GuidedTaskBrief: React.FC<GuidedTaskBriefProps> = ({
  task,
  done,
  total,
  onStart,
  onBack,
}) => {
  const mediaType = task.mediaTypes?.[0];
  const lbl = mediaLabel(mediaType);
  const target = formatTarget(task.estimatedDurationSec);
  const { headline, helper } = renderPromptParts(task);
  const number = done + 1;

  return (
    <div className="gd-screen">
      <div className="gd-brief-header">
        <button className="gd-brief-back" onClick={onBack} aria-label="Back">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M15 6l-6 6 6 6" />
          </svg>
        </button>
        <div className="gd-brief-progress-row">
          <div className="gd-brief-count">
            Task <b>{number}</b> of <b>{total}</b>
          </div>
          <GuidedProgressBar total={total} current={done} />
        </div>
      </div>

      <div className="gd-brief-body">
        <div className="gd-brief-type-chip">
          <span className="gd-bc-ic"><MediaTypeIcon mediaType={mediaType} /></span>
          <span className="gd-bc-lbl">{lbl}</span>
          {target && <span className="gd-bc-dur">{target}</span>}
        </div>

        <div className="gd-brief-question-card">
          <h1 className="gd-brief-question">{headline}</h1>
          {helper && <p className="gd-brief-helper">{helper}</p>}
        </div>

        <div className="gd-brief-footer">
          <div className="gd-brief-meta-row">
            {target ? <span>About <b>{target.replace(' target', '')}</b></span> : <span />}
            <span />
          </div>
          <button className="gd-brief-start" onClick={onStart} type="button">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <circle cx="12" cy="12" r="6" />
            </svg>
            <span>Start task</span>
          </button>
        </div>
      </div>
    </div>
  );
};
